import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getOutstanding, type OutBill, type OutSide } from '@/api/accountsReports';
import { VoucherView } from '../books/VoucherRegisterPage';
import type { ExportCell } from '../exportReport';
import { Loading, PeriodBar, ReportFrame, Seg } from './reportParts';
import { amt, num, r2, tdate, td, th, useExcel, useKeyNav, NO_ACTS } from './reportUtils';

/**
 * Outstandings as on today, as in Tally: Bills Receivable / Payable (bill-wise), Group Outstandings
 * (party totals) and Ageing; a party opens its Ledger Outstandings, a bill its voucher.
 */
type View = 'bills' | 'group' | 'age';

function OutstandingReport({ side }: { side: OutSide }) {
  const [view, setView] = useState<View>('bills');
  const [party, setParty] = useState<number | null>(null);
  const [voucher, setVoucher] = useState<number | null>(null);
  const q = useQuery({ queryKey: ['acc-books', 'reports', 'outstanding', side], queryFn: () => getOutstanding(side) });
  const d = q.data;
  const sfx = side === 'receivable' ? 'Dr' : 'Cr';
  const other = side === 'receivable' ? 'Cr' : 'Dr';
  const sided = (n: number) => (Math.abs(n) > 0.004 ? `${amt(n)} ${n > 0 ? sfx : other}` : '');
  const title = side === 'receivable' ? 'Bills Receivable' : 'Bills Payable';
  const groupLabel = side === 'receivable' ? 'Sundry Debtors' : 'Sundry Creditors';
  const excel = useExcel();

  const partyRow = d?.parties.find((x) => x.ledgerId === party);
  const bills: OutBill[] = useMemo(() => (d ? (party != null ? d.bills.filter((b) => b.ledgerId === party) : d.bills) : []), [d, party]);
  const groups = useMemo(() => {
    const m = new Map<string, NonNullable<typeof d>['parties']>();
    for (const x of d?.parties ?? []) m.set(x.group, [...(m.get(x.group) ?? []), x]);
    return [...m.entries()];
  }, [d]);
  const back = party != null ? () => setParty(null) : undefined;
  const acts = useMemo(() => {
    if (party != null || view === 'bills') return bills.map((b) => (b.voucherId ? () => setVoucher(b.voucherId) : null));
    return groups.flatMap(([, ps]) => [null, ...ps.map((x) => () => setParty(x.ledgerId))]);
  }, [party, view, bills, groups]);
  const { row } = useKeyNav(voucher == null ? acts : NO_ACTS, back);

  const doExcel = () => {
    if (!d) return;
    if (party != null || view === 'bills') {
      return excel(`${title.replace(' ', '_')}${partyRow ? `_${partyRow.party}` : ''}`, title, ['Date', 'Ref. No.', "Party's Name", 'Opening Amount', 'Pending Amount', 'Due on', 'Overdue by days'],
        bills.map((b) => [b.billDate, b.billNo, b.party, b.amount, b.pending, b.dueDate, b.overdue || null]));
    }
    const rows: ExportCell[][] = [];
    for (const [g, ps] of groups) {
      rows.push([g, r2(ps.reduce((s, x) => s + x.net, 0)), ...(view === 'age' ? d.buckets.map((_, k) => r2(ps.reduce((s, x) => s + x.buckets[k], 0))) : [])]);
      for (const x of ps) rows.push([`   ${x.party}`, x.net, ...(view === 'age' ? x.buckets : [])]);
    }
    return excel(`${title.replace(' ', '_')}_${view === 'age' ? 'Ageing' : 'Group'}`, title, ['Particulars', 'Pending Amount', ...(view === 'age' ? d.buckets.map((b) => `${b} days`) : [])], rows);
  };

  let i = 0;
  const billTable = (withParty: boolean) => (
    <table className="w-full min-w-[620px] text-xs">
      <thead>
        <tr className="border-b border-[var(--border-color)] text-left">
          <th className={th}>Date</th><th className={th}>Ref. No.</th>
          {withParty ? <th className={th}>Party&apos;s Name</th> : <th className={`${th} text-right`}>Opening Amount</th>}
          <th className={`${th} text-right`}>Pending Amount</th><th className={th}>Due on</th><th className={`${th} text-right`}>Overdue by days</th>
        </tr>
      </thead>
      <tbody>
        {bills.map((b, k) => (
          <tr key={`${b.ledgerId}-${b.billNo}`} {...row(k)}>
            <td className={`${td} whitespace-nowrap`}>{tdate(b.billDate)}</td>
            <td className={`${td} font-mono`}>{b.billNo}</td>
            {withParty ? <td className={`${td} font-semibold`}>{b.party}</td> : <td className={num}>{sided(b.amount)}</td>}
            <td className={num}>{sided(b.pending)}</td>
            <td className={`${td} whitespace-nowrap`}>{tdate(b.dueDate)}</td>
            <td className={`${num} ${b.overdue ? 'text-danger' : ''}`}>{b.overdue || ''}</td>
          </tr>
        ))}
        {partyRow && Math.abs(partyRow.onAccount) > 0.004 && (
          <tr className="border-b border-[var(--border-color)]"><td className={td} /><td className={`${td} font-mono`}>On Account</td><td className={num} /><td className={num}>{sided(partyRow.onAccount)}</td><td /><td /></tr>
        )}
        {!bills.length && !(partyRow && partyRow.onAccount) && <tr><td colSpan={6} className={`${td} py-6 text-center text-[var(--text-muted)]`}>Nothing pending.</td></tr>}
      </tbody>
      <tfoot>
        <tr className="border-t-2 border-[var(--text-primary)] font-extrabold">
          <td className={td} colSpan={3}>Total{party == null ? ` · ${bills.length} bill${bills.length === 1 ? '' : 's'}` : ''}</td>
          <td className={num}>{sided(partyRow ? partyRow.net : r2(bills.reduce((s, b) => s + b.pending, 0)))}</td><td /><td />
        </tr>
      </tfoot>
    </table>
  );

  const groupTable = (age: boolean) => {
    const buckets = d?.buckets ?? [];
    const gD = r2((d?.parties ?? []).reduce((s, x) => s + Math.max(x.net, 0), 0)), gC = r2((d?.parties ?? []).reduce((s, x) => s + Math.max(-x.net, 0), 0));
    return (
      <table className="w-full text-xs" style={{ minWidth: age ? 260 + 130 * (buckets.length + 1) : 480 }}>
        <thead>
          <tr className="border-b border-[var(--border-color)]">
            <th className={`${th} text-left`}>Particulars</th>
            {age ? [<th key="p" className={`${th} text-right`}>Pending Amount</th>, ...buckets.map((b) => <th key={b} className={`${th} text-right`}>{b} days</th>)]
              : [<th key="d" className={`${th} text-right`}>Debit</th>, <th key="c" className={`${th} text-right`}>Credit</th>]}
          </tr>
        </thead>
        <tbody>
          {groups.flatMap(([g, ps]) => {
            const net = r2(ps.reduce((s, x) => s + x.net, 0));
            const gb = buckets.map((_, k) => r2(ps.reduce((s, x) => s + x.buckets[k], 0)));
            const cells = (n: number, bk: number[], bold: boolean) => (age
              ? [<td key="n" className={`${num} ${bold ? 'font-bold' : ''}`}>{sided(n)}</td>, ...bk.map((x, k) => <td key={k} className={`${num} ${bold ? 'font-bold' : ''}`}>{amt(x)}</td>)]
              : [<td key="d" className={`${num} ${bold ? 'font-bold' : ''}`}>{n > 0 ? amt(n) : ''}</td>, <td key="c" className={`${num} ${bold ? 'font-bold' : ''}`}>{n < 0 ? amt(n) : ''}</td>]);
            const out = [<tr key={`g-${g}`} {...row(i++)}><td className={`${td} font-bold`}>{g}</td>{cells(net, gb, true)}</tr>];
            for (const x of ps) out.push(<tr key={x.ledgerId} {...row(i++)}><td className={`${td} pl-6 italic`}>{x.party}</td>{cells(x.net, x.buckets, false)}</tr>);
            return out;
          })}
          {!groups.length && <tr><td colSpan={age ? buckets.length + 2 : 3} className={`${td} py-6 text-center text-[var(--text-muted)]`}>Nothing pending.</td></tr>}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-[var(--text-primary)] font-extrabold">
            <td className={td}>Grand Total</td>
            {age ? [<td key="n" className={num}>{sided(d?.total ?? 0)}</td>, ...buckets.map((_, k) => <td key={k} className={num}>{amt(r2((d?.parties ?? []).reduce((s, x) => s + x.buckets[k], 0)))}</td>)]
              : [<td key="d" className={num}>{amt(gD)}</td>, <td key="c" className={num}>{amt(gC)}</td>]}
          </tr>
        </tfoot>
      </table>
    );
  };

  return (
    <>
      <PeriodBar noPeriod onBack={back} onExcel={d ? doExcel : undefined}>
        {party == null && <Seg<View> value={view} onChange={setView} options={[['bills', 'Bill-wise'], ['group', 'Group Outstandings'], ['age', 'Ageing']]} />}
        <span className="col-span-3 self-end pb-2 text-[11px] text-[var(--text-muted)] sm:col-auto">As on {tdate(d?.asOn)} · ageing from the bill date</span>
      </PeriodBar>
      <Loading q={q}>
        <ReportFrame
          title={partyRow ? 'Ledger Outstandings' : view === 'bills' ? title : view === 'age' ? 'Ageing Analysis' : 'Group Outstandings'}
          right={partyRow ? partyRow.party : view === 'bills' ? `as on ${tdate(d?.asOn)}` : groupLabel}
          note={party != null || view === 'bills' ? 'Click a bill to open its voucher — or use ↑ ↓ and Enter.' : 'Click a party for its bills — or use ↑ ↓ and Enter.'}
        >
          {party != null || view === 'bills' ? billTable(party == null) : groupTable(view === 'age')}
        </ReportFrame>
      </Loading>
      {voucher != null && <VoucherView id={voucher} onClose={() => setVoucher(null)} />}
    </>
  );
}

export const ReceivablePage = () => <OutstandingReport side="receivable" />;
export const PayablePage = () => <OutstandingReport side="payable" />;
